import { ClipboardCheck, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { ApprovalCard } from '@/components/training/ApprovalCard'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useApproveEnrollment,
  usePendingApprovals,
  useRejectEnrollment,
} from '@/hooks/use-training-approvals'
import { useTenantPath } from '@/lib/tenant'

export default function ApprovalListPage() {
  const tp = useTenantPath()
  const { data, isLoading } = usePendingApprovals()
  const approve = useApproveEnrollment()
  const reject = useRejectEnrollment()

  const approvals = Array.isArray(data) ? data : []

  const handleApprove = async (enrollmentId: string, comment?: string) => {
    try {
      await approve.mutateAsync({ id: enrollmentId, data: comment ? { comment } : undefined })
      toast.success('承認しました')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '承認に失敗しました')
    }
  }

  const handleReject = async (enrollmentId: string, comment?: string) => {
    try {
      await reject.mutateAsync({ id: enrollmentId, data: comment ? { comment } : undefined })
      toast.success('却下しました')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '却下に失敗しました')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">承認一覧</h1>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-6 w-6" />
          <h1 className="text-2xl font-bold">承認一覧</h1>
        </div>
        <Link
          to={tp('/subordinates')}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <Users className="h-4 w-4" />
          部下の受講状況
        </Link>
      </div>

      {approvals.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground">承認待ちの申込はありません</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {approvals.map((a: any) => (
            <ApprovalCard
              key={a.id}
              enrollmentId={a.enrollmentId ?? a.id}
              applicantName={a.applicantName ?? a.user?.name ?? '-'}
              trainingTitle={a.trainingTitle ?? a.training?.title ?? '-'}
              appliedAt={a.createdAt}
              status={a.status}
              onApprove={handleApprove}
              onReject={handleReject}
              isApproving={approve.isPending}
              isRejecting={reject.isPending}
            />
          ))}
        </div>
      )}
    </div>
  )
}
